import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, Typography, ListItemButton, ListItemIcon } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';

// ----------------------------------------------------------------------

const NavItemRoot = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: 12,
  fontWeight: 500,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.05),
    color: theme.palette.text.primary,
  },
}));

const NavIconWrap = styled(ListItemIcon)(({ theme }) => ({
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 10,
  flexShrink: 0,
  marginRight: theme.spacing(1),
  minWidth: 'unset',
  color: 'inherit',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

// ── Section label ────────────────────────────────────────────────────────
const SectionLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  fontSize: '0.65rem',
  color: theme.palette.text.disabled,
  letterSpacing: '0.08em',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(0.5),
  display: 'block',
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active }) {
  const theme = useTheme();
  const isActiveRoot = item ? active(item.path) : false;
  const [open, setOpen] = useState(isActiveRoot);

  if (!item) return null;

  const { title, path, icon, info, children } = item;

  const activeStyles = {
    color: theme.palette.primary.main,
    fontWeight: 700,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  };

  const activeIconStyles = {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  };

  if (children) {
    const validChildren = Array.isArray(children) ? children.filter(Boolean) : [];
    return (
      <>
        <NavItemRoot onClick={() => setOpen((prev) => !prev)} sx={{ ...(isActiveRoot && activeStyles) }}>
          <NavIconWrap sx={{ ...(isActiveRoot && activeIconStyles) }}>{icon}</NavIconWrap>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={isActiveRoot ? 700 : 500} sx={{ color: 'inherit', lineHeight: 1 }}>
              {title}
            </Typography>
          </Box>
          {info && info}
          {open ? (
            <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.5 }} />
          ) : (
            <ChevronRight size={16} style={{ flexShrink: 0, opacity: 0.5 }} />
          )}
        </NavItemRoot>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {validChildren.map((child, index) => {
              if (!child) return null;
              const isActiveSub = active(child.path);
              return (
                <NavItemRoot
                  key={child.title || child.path || index}
                  component={RouterLink}
                  to={child.path}
                  sx={{
                    height: 40,
                    ...(isActiveSub && {
                      color: 'primary.main',
                      fontWeight: 700,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                    }),
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isActiveSub ? 'primary.main' : 'text.disabled',
                      ml: 1.5,
                      mr: 2,
                      flexShrink: 0,
                      transition: 'background-color 0.2s, transform 0.2s',
                      transform: isActiveSub ? 'scale(1.5)' : 'scale(1)',
                    }}
                  />
                  <Typography variant="body2" fontWeight={isActiveSub ? 600 : 400} sx={{ color: 'inherit' }}>
                    {child.title}
                  </Typography>
                </NavItemRoot>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <NavItemRoot component={RouterLink} to={path} sx={{ ...(isActiveRoot && activeStyles) }}>
      <NavIconWrap sx={{ ...(isActiveRoot && activeIconStyles) }}>{icon}</NavIconWrap>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={isActiveRoot ? 700 : 500} sx={{ color: 'inherit', lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      {info && info}
    </NavItemRoot>
  );
}

// ── NavSection ───────────────────────────────────────────────────────────

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);
  const items = Array.isArray(navConfig) ? navConfig.filter(Boolean) : [];

  return (
    <Box {...other}>
      <SectionLabel>Main Menu</SectionLabel>
      <List disablePadding sx={{ px: 1.5 }}>
        {items.map((item, index) => (
          <NavItem key={item?.title || item?.path || index} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
